import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import { FC, useEffect, useState } from 'react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import mediaQueries, { desktopSize, mobileSize, tabletSize } from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { bodyFontSize } from 'src/constants/stylesheet';
import { getAlt } from 'src/utils/legendAlt';
import ViewMonitor from 'src/utils/ViewMonitor';
import Image from './Image';
import { getWindowSize } from './Section';

const useStyles = makeStyles(() =>
  createStyles({
    element: {
      [mediaQueries.Mobile]: {
        flex: `0 0 50%`,
      },
      [mediaQueries.Tablet]: {
        flex: `0 0 ${1 / 3 * 100}%`,
      },
      [mediaQueries.Laptop]: {
        flex: `0 0 ${1 / 3 * 100}%`,
      },
      [mediaQueries.Desktop]: {
        flex: `0 0 ${1 / 3 * 100}%`,
      },
      [mediaQueries.BigDesktop]: {
        flex: `0 0 ${1 / 4 * 100}%`,
      },
    },
    elementInner: {
      boxSizing: 'border-box',

      [mediaQueries.Mobile]: {
        paddingBottom: spacing.Gutter.Mobile,
        paddingLeft: spacing.GutterGrid.Mobile / 2,
        paddingRight: spacing.GutterGrid.Mobile / 2,
      },
      [mediaQueries.Tablet]: {
        paddingBottom: spacing.Gutter.Tablet,
        paddingLeft: spacing.GutterGrid.Mobile / 2,
        paddingRight: spacing.GutterGrid.Mobile / 2,
      },
      [mediaQueries.Laptop]: {
        paddingBottom: spacing.Gutter.Laptop,
        paddingLeft: spacing.GutterGrid.Laptop / 2,
        paddingRight: spacing.GutterGrid.Laptop / 2,
      },
      [mediaQueries.Desktop]: {
        paddingBottom: spacing.Gutter.Desktop,
        paddingLeft: spacing.GutterGrid.Desktop / 2,
        paddingRight: spacing.GutterGrid.Desktop / 2,
      },
      [mediaQueries.BigDesktop]: {
        paddingBottom: spacing.Gutter.BigDesktop,
        paddingLeft: spacing.GutterGrid.BigDesktop / 2,
        paddingRight: spacing.GutterGrid.BigDesktop / 2,
      },
    },
    figure: {
      position: 'relative',
      height: 0,
      paddingBottom: `${2 / 3 * 100}%`,
      overflow: 'hidden',
      marginBottom: 15
    },
    figureInner: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,

      '&:hover img': {
        transform: 'scale(1.05)'
      }
    },
    textOvered: {
      '&$figureInner img': {
        transform: 'scale(1.05)'
      }
    },
    link: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1
    },
    image: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.4s ease-out',
      ...stylesheet.ObjectFitCover
    },
    textContent: {
      position: 'relative'
    },
    textContentLink: {
      position: 'absolute',
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
    },
    artistName: {
      ...stylesheet.BoldText,
    },

    exhibitionTitle: {
      ...stylesheet.BoldText,
      marginBottom: 10,
    },
    subTitle: {
      lineHeight: 1.2,

      [mediaQueries.Mobile]: {
        fontSize: `${18 / bodyFontSize.Mobile}rem`,

        '[data-type="Exhibitions"] &': {
          fontSize: '1rem'
        }
      },
      [mediaQueries.Tablet]: {
        fontSize: `${20 / bodyFontSize.Tablet}rem`,
      },
      [mediaQueries.Laptop]: {
        fontSize: `${20 / bodyFontSize.Laptop}rem`,
      },
      [mediaQueries.Desktop]: {
        fontSize: `${20 / bodyFontSize.Desktop}rem`,
      },
      [mediaQueries.BigDesktop]: {
        fontSize: `${24 / bodyFontSize.BigDesktop}rem`,
      }
    },
    info: {
    },
    hide: {
      ...stylesheet.Hide
    },
    show: {
      ...stylesheet.Show
    }
  }));

export type ExhibitionsPreviewProps = ExhibitionsPreviewsDto &
{
  delayIndex: number;
};

type Props = ExhibitionsPreviewProps;

const ExhibitionsPreview: FC<Props> = ({ OverTitle, Title, Info, PreviewImage, delayIndex, URL }) => {
  const cls = useStyles();
  const [zoomImage, setZoomImage] = useState(false);
  const [columnNumber, setColumnNumber] = React.useState(0);
  const sizes = "(max-width: 768px) 50vw, (min-width: 769px) and (max-width: 1699px) 33vw, (min-width: 1700px) 25vw";

  const windSize = getWindowSize();
  useEffect(() => {
    if (window.innerWidth < mobileSize) {
      setColumnNumber(2);
    }
    if (window.innerWidth >= tabletSize && window.innerWidth < desktopSize) {
      setColumnNumber(3);
    }
    if (window.innerWidth >= desktopSize) {
      setColumnNumber(4);
    }
  }, [windSize]);

  const handleHover = () => {
    setZoomImage(true);
  }
  const handleLeave = () => {
    setZoomImage(false);
  }

  return (
    <ViewMonitor once={true}>{(intersect) =>
      <div
        className={classNames(cls.element, cls.hide, intersect && cls.show)}
        style={{ transitionDelay: `${delayIndex % columnNumber * 100}ms` }}
      >
        <div className={cls.elementInner}>
          <div className={cls.figure}>
            <div className={classNames(cls.figureInner, zoomImage && cls.textOvered)}>
              <Link className={cls.link} to={URL} />
              <Image
                pictureRef={PreviewImage}
                className={cls.image}
                sizes={sizes}
                alt={getAlt(PreviewImage)}
              />
            </div>
          </div>
          <div className={cls.textContent}>
            <Link
              to={URL}
              className={cls.textContentLink}
              onMouseOver={handleHover}
              onMouseLeave={handleLeave}
            />
            {OverTitle &&
              <div className={classNames(cls.artistName, cls.subTitle)}>{OverTitle}</div>
            }
            {Title &&
              <div className={classNames(cls.exhibitionTitle, cls.subTitle)}>{Title}</div>
            }
            {Info &&
              <div className={cls.info} dangerouslySetInnerHTML={{ __html: Info }} />
            }
          </div>
        </div>
      </div>}
    </ViewMonitor>
  );
};

export default ExhibitionsPreview;


// WEBPACK FOOTER //
// ./src/Components/ExhibitionsPreview.tsx